import { useEffect, useRef, useState } from "react";

/**
 * Reveal an element once, the first time it scrolls into view.
 *
 * One shared hook rather than per-section animation, so everything on the page
 * enters the same way — consistency in motion matters as much as in colour, and
 * a page where each section animates differently reads as unfinished.
 *
 * Reveals once and then disconnects: re-animating on every scroll past is the
 * thing that makes marketing pages feel restless.
 *
 * Honours prefers-reduced-motion by starting in the revealed state, so the
 * content is never hidden behind an animation someone has asked not to see.
 */
export default function useReveal({ threshold = 0.15, rootMargin = "0px 0px -8% 0px" } = {}) {
  const ref = useRef(null);
  const [shown, setShown] = useState(() => {
    if (typeof window === "undefined") return true;
    // Also true when IntersectionObserver is missing: content must never be
    // permanently invisible because an API was unavailable.
    if (!("IntersectionObserver" in window)) return true;
    return Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches);
  });

  useEffect(() => {
    if (shown || !ref.current) return;
    const el = ref.current;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          io.disconnect();
        }
      },
      { threshold, rootMargin }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [shown, threshold, rootMargin]);

  return [ref, shown];
}
